import React, { PropsWithChildren, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { P } from '../Texts/Texts';
import { FONT_WEIGHTS } from '../../fundamentals/typography/typography';
import { Icon } from '../Icon/Icon';
import { MATERIAL_ICONS } from '../Icon/icons';
import { PX_TO_REM } from '../../fundamentals/sizes/sizes';
import { IGutterProps, useGutterAsMarginCss, useGutterCss } from '../../fundamentals/spaces/space';
import { IBorderRadiusProps, useBorderRadiusCss } from '../../fundamentals/sizes/borderRadius';
import { IActionCssProps, useActionCss, useFocusCss } from '../../fundamentals/actions';
import { getHexOpacity, resolveProps } from '../../common/utils';
import { KEYS } from '../../common/constants';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseDiv } from '../../common/BaseElements';

enum Mode {
  Default = 'default',
  NoBackground = 'noBackground'
}

interface IAccordionItemProps {
  title?: string;
  open?: boolean;
}

interface IAccordionContext {
  noBackground?: boolean;
}

export interface IAccordionProps extends IAccordionContext, IGutterProps {
  children: React.ReactElement<IAccordionItemProps>[] | React.ReactElement<IAccordionItemProps>;
}

export const accordionColours = ({ palette, base }: IColoursCollection) => ({
  accordionColours: {
    border: `solid ${PX_TO_REM[1]} ${palette.neutral300}`,
    hover: {
      background: `${base.black.colour}${getHexOpacity(10)}`
    },
    active: {
      background: `${base.black.colour}${getHexOpacity(20)}`
    },
    [Mode.Default]: {
      background: base.white.colour,
      selected: {
        background: `${base.black.colour}${getHexOpacity(10)}`
      }
    },
    [Mode.NoBackground]: {
      background: 'transparent',
      selected: {
        background: 'transparent'
      }
    }
  }
});

const getMode = (noBackground?: boolean) => (noBackground ? Mode.NoBackground : Mode.Default);

type AccordionMode = {
  accordionMode: Mode;
};

const AccordionItemSummaryContainer = styled(P)<IGutterProps & AccordionMode & IActionCssProps & { isOpen: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  ${useActionCss};
  ${useGutterCss()};
  ${useFocusCss()};
  ${({ theme, isOpen, accordionMode }) => css`
    background-color: ${isOpen ? theme.accordionColours[accordionMode].selected.background : theme.accordionColours[accordionMode].background};
    &:hover {
      background-color: ${theme.accordionColours.hover.background};
    }
    &:active {
      background-color: ${theme.accordionColours.active.background};
    }
    border-bottom: ${isOpen ? theme.accordionColours.border : `none`};
    border-bottom-left-radius: ${isOpen || accordionMode === Mode.NoBackground ? `none` : `inherit`};
    border-bottom-right-radius: ${isOpen || accordionMode === Mode.NoBackground ? `none` : `inherit`};
  `};
`;

const AccordionItemDescriptionContainer = styled(P)<IGutterProps & AccordionMode>`
  ${useGutterCss()};
  ${({ theme, accordionMode }) => css`
    background-color: ${theme.accordionColours[accordionMode].background};
  `};
`;

const AccordionItemContainer = styled(BaseDiv)<IBorderRadiusProps & AccordionMode & { isOpen: boolean }>`
  ${({ theme }) => css`
    border: ${theme.accordionColours.border};
  `};

  ${({ accordionMode }) =>
    accordionMode === Mode.NoBackground
      ? css`
          border-left: none;
          border-right: none;
        `
      : useBorderRadiusCss()};

  &:first-child {
    ${AccordionItemSummaryContainer} {
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
    }
  }
  &:not(:first-child) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
  &:not(:last-child) {
    border-bottom: none; // doubles up border thickness of not removed
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      &:last-child:not(:only-child) {
        border-radius: 0;
      }
      &:only-child {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    `};
  ${({ isOpen, accordionMode }) =>
    isOpen &&
    accordionMode === Mode.NoBackground &&
    css`
      &:last-child {
        border-bottom: none;
      }
    `};
`;

const AccordionContainer = styled(BaseDiv)<IGutterProps>`
  ${useGutterAsMarginCss()};
`;

const AccordionContext = React.createContext<IAccordionContext>({ noBackground: false });

export const AccordionItem: React.FC<PropsWithChildren<IAccordionItemProps>> = (props) => {
  const contextProps = React.useContext(AccordionContext);
  const resolvedProps = resolveProps({ ...contextProps }, { ...props });
  const { title, open = false, children, noBackground, ...rest } = resolvedProps;
  const { generateTestId } = useTestId(rest, 'AccordionItem');
  const [isOpen, setIsOpen] = useState<boolean>(open);

  const handleKeyUp = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.code === KEYS.ENTER || e.code === KEYS.SPACE) {
      setIsOpen(!isOpen);
    }
  };

  const accordionMode = getMode(noBackground);

  const handleOnclick = (e: React.MouseEvent<HTMLElement>) => {
    setIsOpen(!isOpen);
    e.currentTarget.blur();
  };

  return (
    <AccordionItemContainer {...rest} borderRadius={'large'} accordionMode={accordionMode} isOpen={isOpen} {...generateTestId()}>
      <AccordionItemSummaryContainer
        gutter={24}
        tabIndex={0}
        isOpen={isOpen}
        accordionMode={accordionMode}
        onClick={handleOnclick}
        onKeyUp={handleKeyUp}
        fontWeightCss={FONT_WEIGHTS.SEMI_BOLD}
        {...generateTestId('Summary')}
      >
        {title}
        <Icon name={isOpen ? MATERIAL_ICONS.ExpandLess : MATERIAL_ICONS.ExpandMore}></Icon>
      </AccordionItemSummaryContainer>
      {isOpen && (
        <AccordionItemDescriptionContainer {...rest} gutter={24} accordionMode={accordionMode} {...generateTestId('Details')}>
          {children}
        </AccordionItemDescriptionContainer>
      )}
    </AccordionItemContainer>
  );
};

export const Accordion: React.FC<IAccordionProps> = ({ children, noBackground = false, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'Accordion');
  return (
    <AccordionContainer {...generateTestId()} {...rest}>
      <AccordionContext.Provider value={{ noBackground }}>{children}</AccordionContext.Provider>
    </AccordionContainer>
  );
};
